/**
 * Step 3: Activity Tracking - Weekly vs Daily variant selection
 * Includes work/job activity type for accurate calorie calculation
 */
import React from 'react';
import { CheckCircle, Briefcase, Dumbbell, Monitor, Presentation, Footprints, Wrench, Package, HardHat, Moon, PersonStanding, Activity, TrendingUp, CalendarDays, BarChart3 } from 'lucide-react';
import { Label } from '@/components/ui/label';

// Activity levels for Variant A (Weekly) - SPORT only
const ACTIVITY_LEVELS = [
  { id: 'sedentary', label: 'Kein Sport', description: 'Kein regelmässiges Training', multiplier: 1.2 },
  { id: 'light', label: 'Leicht aktiv', description: '1-3x Sport pro Woche', multiplier: 1.375 },
  { id: 'moderate', label: 'Moderat aktiv', description: '3-5x Sport pro Woche', multiplier: 1.55 },
  { id: 'active', label: 'Sehr aktiv', description: '6-7x Sport pro Woche', multiplier: 1.725 },
  { id: 'very_active', label: 'Extrem aktiv', description: 'Tägliches intensives Training', multiplier: 1.9 },
];

// NEW: Work/Job activity levels - separate from sport
const WORK_ACTIVITY_LEVELS = [
  { id: 'buero', label: 'Büro / Homeoffice', description: 'Überwiegend sitzend', kcal_bonus: 0, icon: Monitor },
  { id: 'leicht_stehend', label: 'Leicht stehend', description: 'Lehrer, Verkäufer, Empfang', kcal_bonus: 200, icon: Presentation },
  { id: 'stehend_gehend', label: 'Stehend & Gehend', description: 'Kellner, Friseur, Pflege', kcal_bonus: 350, icon: Footprints },
  { id: 'koerperlich_leicht', label: 'Körperlich leicht', description: 'Handwerk, Einzelhandel', kcal_bonus: 500, icon: Wrench },
  { id: 'koerperlich_mittel', label: 'Körperlich mittel', description: 'Lager, Gärtner, Mechaniker', kcal_bonus: 650, icon: Package },
  { id: 'koerperlich_schwer', label: 'Körperlich schwer', description: 'Bau, Umzug, Landwirtschaft', kcal_bonus: 800, icon: HardHat },
];

// Daily activity levels for Variant B (Daily)
const DAILY_ACTIVITY_LEVELS = [
  { id: 'sehr_schlecht', label: 'Sehr Schlecht', description: 'Kaum Bewegung, viel sitzen', kcal_adjustment: 0, icon: Moon },
  { id: 'schlecht', label: 'Schlecht', description: 'Wenig Bewegung', kcal_adjustment: 30, icon: Footprints },
  { id: 'durchschnittlich', label: 'Durchschnittlich', description: 'Normale Tagesaktivität', kcal_adjustment: 60, icon: PersonStanding },
  { id: 'gut', label: 'Gut', description: 'Aktiver Alltag', kcal_adjustment: 90, icon: Activity },
  { id: 'sehr_gut', label: 'Sehr Gut', description: 'Viel Bewegung im Alltag', kcal_adjustment: 120, icon: TrendingUp },
  { id: 'profi', label: 'Profi', description: 'Körperlich anspruchsvoller Job', kcal_adjustment: 150, icon: Dumbbell },
];

// Activity tracking variants
const ACTIVITY_VARIANTS = [
  { 
    id: 'weekly', 
    label: 'Wöchentlich', 
    icon: CalendarDays,
    badge: 'Empfohlen',
    description: 'Training ist in der Wochenberechnung inkludiert',
    details: ['Einfache Einrichtung', 'Gleichbleibende Kalorien']
  },
  { 
    id: 'daily', 
    label: 'Täglich', 
    icon: BarChart3,
    badge: 'Für Profis',
    description: 'Tägliche Anpassung nach Aktivität',
    details: ['Trainings separat erfasst', 'Nachbrenn-Effekt sichtbar']
  },
];

const StepActivityTracking = ({ prefs, setPrefs }) => {
  return (
    <div className="space-y-4" data-testid="onboarding-step-activity">
      <div className="text-center mb-6">
        <svg className="w-12 h-12 text-green-500 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
        <h3 className="text-xl font-bold text-white">Wie trackst du deine Aktivität?</h3>
        <p className="text-zinc-400 text-sm">Wähle deinen bevorzugten Tracking-Modus</p>
      </div>
      
      {/* Varianten-Auswahl */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {ACTIVITY_VARIANTS.map(variant => (
          <button
            key={variant.id}
            onClick={() => setPrefs(prev => ({ ...prev, activity_variant: variant.id }))}
            className={`p-4 rounded-xl border-2 text-left transition-all relative ${
              prefs.activity_variant === variant.id
                ? 'border-green-500 bg-green-500/10'
                : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
            }`}
          >
            {variant.badge && (
              <span className={`absolute -top-2 -right-2 text-xs px-2 py-0.5 rounded-full ${
                variant.id === 'weekly' ? 'bg-green-500 text-black' : 'bg-blue-500 text-white'
              }`}>
                {variant.badge}
              </span>
            )}
            <variant.icon className="w-7 h-7 mb-2 text-green-400" />
            <div className="font-semibold text-white">{variant.label}</div>
            <p className="text-xs text-zinc-400 mt-1">{variant.description}</p>
            <ul className="mt-2 space-y-1">
              {variant.details.map((detail, i) => (
                <li key={i} className="text-xs text-zinc-500">{detail}</li>
              ))}
            </ul>
          </button>
        ))}
      </div>
      
      {/* Variante A: Wöchentliches Aktivitätslevel */}
      {prefs.activity_variant === 'weekly' && (
        <div className="space-y-4">
          {/* Sport-Aktivität */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-sm flex items-center gap-2">
              <span className="text-lg"><Dumbbell className="w-4 h-4" /></span> Wie oft trainierst du pro Woche?
            </Label>
            {ACTIVITY_LEVELS.map(level => (
              <button
                key={level.id}
                onClick={() => setPrefs(prev => ({ ...prev, activity_level: level.id }))}
                className={`w-full p-3 rounded-lg border-2 text-left transition-all flex justify-between items-center ${
                  prefs.activity_level === level.id
                    ? 'border-green-500 bg-green-500/10'
                    : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                }`}
              >
                <div>
                  <span className="font-medium text-white">{level.label}</span>
                  <p className="text-xs text-zinc-400">{level.description}</p>
                </div>
                {prefs.activity_level === level.id && (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                )}
              </button>
            ))}
          </div>
          
          {/* Hinweis auf Tagesablauf */}
          <div className="mt-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <p className="text-xs text-amber-400">
              <strong>Tipp:</strong> Im nächsten Schritt erfasst du deinen detaillierten Tagesablauf 
              (Arbeit, Freizeit, Schlaf) für eine noch präzisere Kalorienberechnung.
            </p>
          </div>
        </div>
      )}
      
      {/* Variante B: Tägliche Alltagsaktivität */}
      {prefs.activity_variant === 'daily' && (
        <div className="space-y-2">
          <Label className="text-zinc-300 text-sm">Dein typischer Alltag (Trainings werden separat erfasst)</Label>
          <div className="grid grid-cols-2 gap-2">
            {DAILY_ACTIVITY_LEVELS.map(level => (
              <button
                key={level.id}
                onClick={() => setPrefs(prev => ({ ...prev, daily_activity_level: level.id }))}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  prefs.daily_activity_level === level.id
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <level.icon className="w-5 h-5 text-blue-400 shrink-0" />
                  <span className="font-medium text-white text-sm">{level.label}</span>
                </div>
                <p className="text-xs text-zinc-400">{level.description}</p>
                <p className="text-xs text-blue-400 mt-1">+{level.kcal_adjustment} kcal</p>
              </button>
            ))}
          </div>
          
          {/* Info Box für Variante B */}
          <div className="mt-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
            <p className="text-xs text-blue-400">
              <strong>Nachbrenn-Effekt:</strong> Bei dieser Variante werden die Kalorien deiner 
              Trainings separat erfasst und der Nachbrenn-Effekt vom Vortag wird angezeigt.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export { ACTIVITY_LEVELS, DAILY_ACTIVITY_LEVELS, ACTIVITY_VARIANTS, WORK_ACTIVITY_LEVELS };
export default StepActivityTracking;
